import { useEffect } from 'react';
import { Link } from 'react-router-dom';

import QuoteDisplay from '@/components/QuoteDisplay/QuoteDisplay';
import { useCurrentQuote, useQuoteLoading, useQuoteError, useFetchQuote } from '@/store/quoteStore';

/**
 * Home page component that displays the main Primatif interface
 * 
 * Features:
 * - Large "Primatif" heading with brand styling
 * - Glassmorphic "Learn More" button with glow effects
 * - Quote display container with black glass styling
 * - Automatic quote fetching on mount
 * - Hourly quote refresh system that syncs with server
 * - Responsive design with proper spacing and typography
 * 
 * The page implements a smart refresh system that:
 * - Fetches quotes immediately on component mount
 * - Calculates time until next hour and sets up automatic refresh
 * - Uses setInterval for continuous hourly updates
 * - Cleans up timers on component unmount
 * 
 * @returns JSX element containing the complete home page interface
 */
function HomePage() {
  const currentQuote = useCurrentQuote();
  const isLoading = useQuoteLoading();
  const error = useQuoteError();
  const fetchQuote = useFetchQuote();

  // Fetch quote on component mount
  useEffect(() => {
    fetchQuote();
  }, [fetchQuote]);

  // Implement hourly refresh logic
  useEffect(() => {
    const now = new Date();
    const minutesUntilNextHour = 60 - now.getMinutes();
    const secondsUntilNextHour = minutesUntilNextHour * 60 - now.getSeconds();

    const timeoutId = setTimeout(() => {
      fetchQuote(); // Fetch immediately at the top of the next hour
      // Then set up interval for every hour thereafter
      const intervalId = setInterval(() => {
        fetchQuote();
      }, 60 * 60 * 1000); // Every hour
      return () => clearInterval(intervalId);
    }, secondsUntilNextHour * 1000);

    return () => clearTimeout(timeoutId);
  }, [fetchQuote]);

  return (
    <main className="flex-grow">
      {/* Hero Section */}
      <section className="w-full py-20 md:py-32 lg:py-40 bg-brand-background">
        <div className="container-main text-center">
          <div className="space-y-4">
            <h1 className="font-heading text-6xl font-bold tracking-wider text-brand-primary sm:text-7xl md:text-8xl">
              Primatif
            </h1>
            <Link to="/about" className="relative inline-flex items-center px-8 py-4 text-base font-bold text-brand-primary bg-gradient-to-br from-black/30 via-black/20 to-black/30 backdrop-blur-xl border border-white/20 rounded-lg transition-all duration-700 ease-in-out hover:shadow-[0_0_30px_8px_rgba(239,68,68,0.6)] hover:animate-pulse before:absolute before:inset-0 before:rounded-lg before:bg-gradient-to-br before:from-white/8 before:via-transparent before:to-transparent before:opacity-40 before:pointer-events-none">
              Learn More
            </Link>
          </div>

          <div className="mx-auto mt-6 max-w-3xl">
            <div className="relative rounded-lg border border-white/20 bg-gradient-to-br from-black/30 via-black/20 to-black/30 backdrop-blur-xl p-6 shadow-2xl shadow-black/60 before:absolute before:inset-0 before:rounded-lg before:bg-gradient-to-br before:from-white/8 before:via-transparent before:to-transparent before:opacity-40 before:pointer-events-none">
              <QuoteDisplay quote={currentQuote} isLoading={isLoading} error={error} />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

export default HomePage;
